/**
 * Из чего собран ролик (§20).
 *
 * Просьба клиента: «желательно видеть разбор, на чём было создано видео».
 * Раньше ролик приходил файлом, и что в нём чей голос, какой образ пошёл
 * в кадр и откуда графика — выяснялось только пересмотром сцен по одной.
 *
 * Показываем продуктовые вещи: §41 запрещает называть поставщиков,
 * да человека и интересует не это, а что попало в кадр.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { projectsApi } from '../../api/projects';

interface BreakdownPanelProps {
  projectId: string;
  /** Пересобранный ролик меняет разбор — обновляем вместе с ним. */
  version: string;
}

export function BreakdownPanel({ projectId, version }: BreakdownPanelProps) {
  const { t } = useTranslation();

  const breakdown = useQuery({
    queryKey: ['project-breakdown', projectId, version],
    queryFn: () => projectsApi.breakdown(projectId),
  });

  const data = breakdown.data;
  if (!data || data.scenes.length === 0) return null;

  const summary = [
    t('breakdown.duration', { value: Math.round(data.durationSec) }),
    data.aspectRatio,
    t('breakdown.spent', { value: data.spentUnits }),
    data.captionsEnabled ? t('breakdown.withCaptions') : null,
    data.musicEnabled ? t('breakdown.withMusic') : null,
  ].filter(Boolean);

  return (
    <section className="flex flex-col gap-3 rounded-card border border-line bg-surface p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-card-title">{t('breakdown.title')}</p>
        <p className="text-meta text-content-muted">{summary.join(' · ')}</p>
      </div>

      <ul className="flex flex-col gap-2">
        {data.scenes.map((scene, index) => {
          // Что пошло в сцену: голос, образ, графика, титры — словами.
          const parts = [
            scene.hasVoice ? t('breakdown.yourVoice') : null,
            scene.lookName,
            scene.graphicsLabel,
            scene.hasCaptions ? t('breakdown.captions') : null,
          ].filter(Boolean);

          return (
            <li
              key={scene.order}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-line pt-2 first:border-0 first:pt-0"
            >
              <span className="text-meta text-content-muted">{index + 1}</span>
              <span className="text-body text-content">{scene.typeLabel}</span>
              <span className="text-meta text-content-muted">
                {t('scene.seconds', { value: Math.round(scene.durationSec) })}
              </span>
              <span className="text-meta text-content-secondary">{parts.join(' · ')}</span>
              {scene.spentUnits > 0 ? (
                <span className="ml-auto text-meta text-content-muted">
                  {t('scene.unitsShort', { value: scene.spentUnits })}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
